import { supabase } from "@/integrations/supabase/client";
import { hasAnalyticsConsentNow } from "@/lib/cookieConsent";

/**
 * Anonieme meting van het sollicitatieformulier (QuickApplyDialog).
 * - qa_session: willekeurige ID per opening, alleen in het geheugen (geen cookie/storage).
 * - Nooit naam, e-mail, telefoon, woonplaats of user-id.
 * - Koppeling met de bezoekerssessie alleen na analytics-toestemming.
 */
export type QuickApplyEvent =
  | "quick_apply_opened"
  | "quick_apply_step1_completed"
  | "quick_apply_password_viewed"
  | "quick_apply_password_error"
  | "quick_apply_email_exists"
  | "quick_apply_submitted"
  | "quick_apply_failed"
  | "quick_apply_closed";

export type InAppBrowser = "instagram" | "facebook" | "tiktok" | "other" | "none";

export function detectInAppBrowser(ua: string): InAppBrowser {
  if (/Instagram/i.test(ua)) return "instagram";
  if (/FBAN|FBAV|FB_IAB|FBIOS|Messenger/i.test(ua)) return "facebook";
  if (/musical_ly|TikTok|BytedanceWebview|trill/i.test(ua)) return "tiktok";
  if (/; wv\)|Snapchat|LinkedInApp|Twitter|Pinterest|Line\//i.test(ua)) return "other";
  return "none";
}

export function detectDevice(ua: string, width?: number): "mobile" | "desktop" {
  if (/Mobi|Android|iPhone|iPod|iPad/i.test(ua)) return "mobile";
  if (typeof width === "number" && width > 0 && width < 768) return "mobile";
  return "desktop";
}

export type QuickApplySource = {
  source: string;
  utm_source: string | null;
  utm_campaign: string | null;
  boost_id: string | null;
};

export function readSource(search: string): QuickApplySource {
  const p = new URLSearchParams(search);
  const clip = (v: string | null) => (v ? v.slice(0, 80) : null);
  const utm_source = clip(p.get("utm_source"));
  const utm_campaign = clip(p.get("utm_campaign"));
  const boost_id = clip(p.get("boost_id") || p.get("boost"));
  const source = boost_id ? "boost" : utm_source ? utm_source.toLowerCase() : "direct";
  return { source, utm_source, utm_campaign, boost_id };
}

export type PasswordErrorCode = "too_short" | "weak_password" | "pwned" | "other";

export function classifyPasswordError(code: string | null | undefined, length?: number): PasswordErrorCode {
  if (typeof length === "number" && length < 8) return "too_short";
  if (code === "weak_password") return "weak_password";
  if (code === "pwned" || code === "password_pwned") return "pwned";
  return "other";
}

const ALLOWED_KEYS = new Set([
  "qa_session", "job_id", "source", "utm_source", "utm_campaign", "boost_id",
  "device", "in_app", "ms_since_open", "consent", "visitor_session",
  "code", "step", "sent",
]);

/** Laat alleen toegestane, niet-persoonlijke velden door. */
export function buildEventData(input: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(input)) {
    if (ALLOWED_KEYS.has(k) && v !== undefined) out[k] = v;
  }
  return out;
}

export function newQaSession(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `qa_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
  }
}

export type QuickApplyContext = { qaSession: string; jobId: string; openedAt: number };

export function trackQuickApply(
  event: QuickApplyEvent,
  ctx: QuickApplyContext,
  extra: { code?: string; step?: number; sent?: boolean } = {},
): void {
  try {
    const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
    const consent = hasAnalyticsConsentNow();
    const visitor = consent ? sessionStorage.getItem("visitor_session_id") : null;
    const src = readSource(window.location.search);
    const data = buildEventData({
      qa_session: ctx.qaSession,
      job_id: ctx.jobId,
      ...src,
      device: detectDevice(ua, window.innerWidth),
      in_app: detectInAppBrowser(ua),
      ms_since_open: Math.max(0, Date.now() - ctx.openedAt),
      consent,
      visitor_session: visitor ?? undefined,
      ...extra,
    });
    void supabase
      .from("funnel_events")
      .insert({
        session_id: visitor || ctx.qaSession,
        user_id: null,
        event_type: event,
        event_data: data as never,
        page_path: window.location.pathname,
      })
      .then(() => undefined, () => undefined);
  } catch {
    /* meting mag het formulier nooit hinderen */
  }
}

let campaignPageSession: string | null = null;

/** Anonymous visit of an ad page (/vacatures/:slug/campagne): the first step of the landing page funnel in Ads data. */
export function trackCampaignPageView(jobId: string): void {
  try {
    if (!campaignPageSession) campaignPageSession = newQaSession();
    const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
    const data = buildEventData({
      job_id: jobId,
      ...readSource(window.location.search),
      device: detectDevice(ua, window.innerWidth),
      in_app: detectInAppBrowser(ua),
      consent: hasAnalyticsConsentNow(),
    });
    void supabase
      .from("funnel_events")
      .insert({ session_id: campaignPageSession, user_id: null, event_type: "campaign_page_viewed", event_data: data as never, page_path: window.location.pathname })
      .then(() => undefined, () => undefined);
  } catch {
    /* meting mag de pagina nooit hinderen */
  }
}
